// Photohawk's face data policy at /face-data-policy (English, the source of truth): one official page with a
// separate, clearly labelled policy for each product (owner decision, 20 October 2026).
//
// Facts (keep in sync, don't re-derive):
//   * Photohawk Studio: src/content/legal/biometric.en.ts (/biometric-data-retention), the privacy policy §4
//     (src/content/legal/gdpr.en.ts, and v1_0/gdpr.en.ts until 1 November 2026) and photohawk-serverless
//     docs/face-data-retention.md.
//   * Photohawk Family: src/content/personalLegal/face-data-policy.md (synced from photohawk-personal
//     legal/face-data-policy.md by scripts/sync-personal-legal.sh; the full text at /face-data-policy/family), the
//     app's consent wording (photohawk-personal lib/src/privacy/consent_copy.dart) and photohawk-serverless
//     docs/cloud-face-matching.md.
//   * Studio §5 (legal basis, version 1.1) is mirrored by the privacy policy's §4 legal basis, §5 face-search bullet
//     and §13 (gdpr.<lang>.ts and v1_0/gdpr.<lang>.ts): the administrator decides whether face search is appropriate;
//     the consent of the people photographed only where local law requires it, otherwise bib and time search.
// Wording rules: say "the administrator" / "the gallery's administrator" (never photographer or organiser), never
// claim the law requires 12 months, and never let the two products' data read as shared.
import type { LegalSection } from "@/content/legal/types";

export interface ProductPolicy {
  id: "studio" | "family";
  /** The tab's label. */
  tab: string;
  /** The section heading (the product and what it is for). */
  heading: string;
  /** One plain paragraph under the heading. */
  summary: string;
  sections: LegalSection[];
  /** The full, detailed document for this product. */
  full: { text: string; label: string; href: string };
}

export interface FaceDataPolicyCopy {
  seo: { title: string; description: string };
  title: string;
  version: string;
  effectiveDate: string;
  /** "Version {version} · Effective {date}". */
  meta: string;
  intro: string[];
  tabsLabel: string;
  studio: ProductPolicy;
  family: ProductPolicy;
  changelog: { title: string; items: string[] };
  /** The link the other legal pages (/privacy, /biometric-data-retention) show to this page. */
  seeAlso: { text: string; label: string };
  /** The full Family policy page (/face-data-policy/family): its <title>/description and the link back. */
  familyPage: { seoTitle: string; description: string; back: string; englishOnly: string };
}

const CONTACT =
  "CLOUDCONCUR LTD (company no. 13399913), trading as Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom. Email: support@photohawk.com.";

const en: FaceDataPolicyCopy = {
  seo: {
    title: "Face data policy | Photohawk",
    description:
      "What face data Photohawk makes, where it is processed, what it is used for and when it is deleted: one policy for Photohawk Studio (event and business galleries) and one for the Photohawk app (the app for the photos of your people).",
  },
  title: "Face data policy",
  version: "1.1",
  effectiveDate: "22 October 2026",
  meta: "Version {version} · Effective {date}",
  intro: [
    "Photohawk makes two products that find people in photos, and each has its own face data policy below. Photohawk Studio runs event and business galleries, where people find their own photos with a selfie. The Photohawk app is for the photos of your people, on your own phone.",
    "The two are kept apart. Face data from Photohawk Studio galleries and face data from the Photohawk app are never shared, mixed or matched with each other. Neither is ever sold or used for advertising, and face data is never a photo: deleting it never deletes a photo.",
  ],
  tabsLabel: "Choose a product",
  studio: {
    id: "studio",
    tab: "Photohawk Studio",
    heading: "Photohawk Studio — event and business galleries",
    summary:
      "Selfie search is optional and is switched on per gallery by its administrator. It lets a person find their own photos in that gallery with a selfie.",
    sections: [
      {
        heading: "1. What face data is made",
        blocks: [
          {
            type: "ul",
            items: [
              "Face templates: numerical representations of the faces detected in a gallery's photos, held in that gallery's face-search index (Amazon Rekognition) or, for galleries on one of our own face models, as face vectors.",
              "The face archive: small face crops kept privately so a gallery's face search can be rebuilt without re-reading the original photos. It is never shown to anyone or served publicly.",
              "Selfies used to search are not face data we keep: a selfie is compared with that gallery's face templates and then discarded. It is not stored and no face template is made from it.",
            ],
          },
          {
            type: "p",
            text: "Finding people in photos (whole bodies, without identifying anyone) and reading bib numbers and text do not use face data. Bib/number search is not affected by this policy.",
          },
        ],
      },
      {
        heading: "2. Where it is processed",
        blocks: [
          {
            type: "ul",
            items: [
              "In Photohawk's cloud on Amazon Web Services in the European Union (Ireland, eu-west-1), after a photo is uploaded.",
              "Each gallery has its own face-search index (an Amazon Rekognition collection). A selfie is only ever searched against the gallery it was taken for.",
              "To save time, Photohawk Studio's upload apps may find faces on the computer or phone doing the upload and send small face crops with the photo. Face templates are always made in our cloud.",
            ],
          },
        ],
      },
      {
        heading: "3. What it is used for, and never used for",
        blocks: [
          {
            type: "p",
            text: "Face data is used only so that people can find their own photos in that gallery with a selfie.",
          },
          {
            type: "p",
            text: "It is never sold, never used for advertising, never used to identify anyone outside the gallery, and never mixed with or matched against the Photohawk app's face data.",
          },
        ],
      },
      {
        heading: "4. How long it is kept, and when it is deleted",
        blocks: [
          {
            type: "p",
            text: "From 1 November 2026 face data is permanently deleted at the earliest of:",
          },
          {
            type: "ul",
            items: [
              "12 months after the photo was uploaded (photo by photo, even if the gallery stays online);",
              "30 days after the gallery is closed or deleted, or after face search is switched off for it (cancelled if the administrator reopens it or switches face search back on within those 30 days);",
              "30 days after a person asks us to delete their face data or withdraws their consent;",
              "45 days after our yearly review finds it is no longer necessary.",
            ],
          },
          {
            type: "p",
            text: "Until 31 October 2026, face data is deleted when a gallery ages out (within about 60 days) and when a gallery is deleted. Face templates are not backed up, so a deletion is final; our database backups hold only references to faces (never templates) and are purged within 35 days. Photos, sales, downloads and bib search are not affected.",
          },
          {
            type: "link",
            text: "The full retention schedule and how deletion works:",
            label: "Biometric data retention policy",
            href: "/biometric-data-retention",
          },
        ],
      },
      {
        heading: "5. Legal basis",
        blocks: [
          {
            type: "p",
            text: "Photos. Photos in a gallery are shared so participants can find and buy their own photos. They are processed because that is necessary for the gallery's purpose, and kept only as long as is proportionate to it (see §4).",
          },
          {
            type: "p",
            text: "Face search. Face data is biometric data and gets extra protection:",
          },
          {
            type: "ul",
            items: [
              "When it is used: only so that people can find their own photos in that gallery. It is never used to identify anyone for any other purpose.",
              "How long it is kept: only for a limited time (§4). It is deleted as soon as it is no longer needed.",
              "The person searching is asked for their explicit consent before their selfie is used. The selfie is never stored.",
              "The administrator decides whether face search is appropriate for their event and permitted where it takes place. Where local law requires the consent of the people photographed, the administrator must have it, for example through event registration. Where they don't, they should use bib and time search instead, which uses no face data.",
              "Anyone can ask for their face data to be deleted at any time, and it is deleted within 30 days (§8).",
            ],
          },
          {
            type: "p",
            text: "For people in a gallery, the gallery's administrator is the controller; Photohawk processes face data on their behalf under our data processing agreement.",
          },
        ],
      },
      {
        heading: "6. Illinois and other US states",
        blocks: [
          {
            type: "p",
            text: "This policy and the biometric data retention policy are our public written retention schedule. The deletion limits in §4 fall within the deadlines of the Illinois Biometric Information Privacy Act (BIPA: when the purpose is satisfied or within 3 years of the person's last interaction), the Texas CUBI, the Colorado Privacy Act and Washington's RCW 19.375. The 12-month limit is Photohawk's own choice; no law prescribes it.",
          },
        ],
      },
      {
        heading: "7. Who processes it",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition and storage), EU (Ireland) region, as our sub-processor, only on our instructions. We don't disclose face data to anyone else unless the law requires it.",
          },
        ],
      },
      {
        heading: "8. Requests and contact",
        blocks: [
          {
            type: "ul",
            items: [
              "In the gallery: open selfie search and choose “Make a request”. It goes to the gallery's administrator and to Photohawk.",
              "By email: support@photohawk.com, telling us which gallery or event it concerns. We act on the request within 30 days.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "The detailed Studio retention schedule (English):",
      label: "Biometric data retention policy",
      href: "/biometric-data-retention",
    },
  },
  family: {
    id: "family",
    tab: "Photohawk app",
    heading: "The Photohawk app — for the photos of your people",
    summary:
      "Face grouping is optional. The app (in the app stores as “Photohawk”) asks before it makes any face data, and faces are found on your phone.",
    sections: [
      {
        heading: "1. What face data is made",
        blocks: [
          {
            type: "ul",
            items: [
              "With face grouping on, on your phone: face templates (a list of numbers for each face found), where the faces are in each photo or video, and which faces belong to the same person. The names and corrections you add stay only on your phone.",
              "With cloud face matching on as well: small face cut-outs (never whole photos or videos) and the face templates and groups our cloud service makes from them.",
            ],
          },
          {
            type: "p",
            text: "Finding people (whole bodies, without identifying anyone) and things in your photos does not use face data and works with face grouping off.",
          },
        ],
      },
      {
        heading: "2. Where it is processed",
        blocks: [
          {
            type: "ul",
            items: [
              "On your phone: faces are found on the phone, and face data is kept in the app's private storage, excluded from phone backups.",
              "In the cloud, only with your consent to cloud face matching: face cut-outs go to your own face collection at Amazon Rekognition (Amazon Web Services, EU (Ireland) region), which holds only your faces. The cut-outs are deleted as soon as they are matched (anything left is deleted after a day).",
              "Without that consent, everything stays on your phone. We never receive your photos or videos, and never the names you give people.",
            ],
          },
        ],
      },
      {
        heading: "3. What it is used for, and never used for",
        blocks: [
          {
            type: "p",
            text: "Face data is used only to group your photos and videos by the people in them, to let you search by person, and to help you choose which photos to share.",
          },
          {
            type: "p",
            text: "It is never sold, never used for advertising, profiling or identity checks, never used to identify anyone outside your own photos, never matched across different users, never used to train models, and never mixed with or matched against Photohawk Studio's face data. It is never part of a share.",
          },
        ],
      },
      {
        heading: "4. How long it is kept, and when it is deleted",
        blocks: [
          {
            type: "p",
            text: "On your phone, face data is kept until you turn face grouping off (or tap “Delete all face data”), delete the app, or don't use Photohawk for 3 years. A photo's face data goes when the photo leaves your phone.",
          },
          {
            type: "p",
            text: "In the cloud, face data is deleted when you turn cloud face matching or face grouping off, when you delete your Photohawk account, when you ask us to (within 30 days), after 3 years without using Photohawk, and 30 days after your plan ends unless you subscribe again before then.",
          },
          {
            type: "p",
            text: "Cloud deletion removes your whole face collection. Deleting face data never deletes photos or videos.",
          },
        ],
      },
      {
        heading: "5. Legal basis and consent",
        blocks: [
          {
            type: "p",
            text: "We rely on your explicit consent (GDPR Art. 9(2)(a), and the equivalent in other laws), asked for twice and separately: first for face grouping on your phone (the box starts unticked; “Not now” means no face detection at all), then for cloud face matching (“Continue”, or “Keep it on my phone only”). You can change either at any time in the app's settings. If the wording changes, the app asks again.",
          },
        ],
      },
      {
        heading: "6. Illinois",
        blocks: [
          {
            type: "p",
            text: "Face grouping, and so cloud face matching, is not offered to people who tell the app they live in Illinois (the app asks; it does not use GPS or your IP address). Our service refuses cloud matching for Illinois too, and if you change where you live to Illinois, existing face data is deleted on the phone and in the cloud.",
          },
        ],
      },
      {
        heading: "7. Who processes it",
        blocks: [
          {
            type: "p",
            text: "Only with cloud face matching: Amazon Web Services (Amazon Rekognition), EU (Ireland) region, as our processor, only on our instructions. Face data on your phone is never sent to anyone.",
          },
        ],
      },
      {
        heading: "8. Requests and contact",
        blocks: [
          {
            type: "ul",
            items: [
              "In the app: turn cloud face matching or face grouping off, or tap “Delete all face data”.",
              "By email: support@photohawk.com, to access or delete your face data in the cloud.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "The full Photohawk app face data policy, with its retention schedule (English):",
      label: "Photohawk app: Face Data Policy",
      href: "/face-data-policy/family",
    },
  },
  changelog: {
    title: "Changes",
    items: [
      "1.1, 22 October 2026: Photohawk Studio, section 5 rewritten: what the legal basis is for photos and for face search, and what the administrator decides.",
      "1.0, 20 October 2026: first version. One page with a separate policy for Photohawk Studio and for Photohawk Family; the Photohawk Family face data policy is no longer a draft.",
    ],
  },
  seeAlso: {
    text: "How face data is handled in Photohawk Studio and in the Photohawk app:",
    label: "Face data policy",
  },
  familyPage: {
    seoTitle: "Photohawk app: Face Data Policy | Photohawk",
    description:
      "The full face data policy of the Photohawk app: what face data it makes, where it is kept, and its retention and destruction schedule.",
    back: "Face data policy for both products",
    englishOnly: "",
  },
};

export default en;
